import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { getCookie, sanitizeData } from "../utils/Common";
import { ObjectType } from "../types/tableTypes";
import { LoansContext } from "../context/LoansContext";
import {
  getPricingAdjustmentAuditFieldPath,
  getLeverageAdjustmentAuditFieldPath,
} from "../constants/auditFields";
import AuditTag from "./common/AuditTag";

// "Final Price Adjustment" is the one row that's a computed sum, not an
// independently overridable adjustment — kept read-only even in edit mode,
// matching how LeverageTable already special-cases it for bolding/coloring.
const TOTAL_ROW_LABEL = "Final Price Adjustment";

// The Leverages card's "Adjusted Max" row (ltvAsIs/ltvARV/ltc/lttc) is
// always a percentage — see getLeverageTableBody in utils/Common.ts, which
// formats every value through getPercentageValue as "<number> %" before it
// ever reaches this table. leverageAdjustedMaxOverrides, the guideline API
// payload, and the audit trail all already expect that exact "<number> %"
// shape (see mappings.test.ts / common.test.ts fixtures), so the input only
// hides/restores the " %" suffix for editing — it never changes the stored
// format.
const stripPercentSuffix = (value: string): string => {
  // Linear-time equivalent of /\s*%\s*$/ (avoids the ReDoS-prone pattern):
  // trim trailing whitespace, drop a single trailing '%', then trim again.
  const trimmed = value.trimEnd();
  return trimmed.endsWith("%") ? trimmed.slice(0, -1).trimEnd() : value;
};

// Keeps only digits and a single decimal point, capped at 2 fractional
// digits, as the user types — matching the 2-decimal precision
// getPercentageValue already formats every leverage percentage to (e.g.
// "62.50 %"), so the field can't be saved holding letters, symbols, or more
// precision than the rest of the app ever displays for this value.
const sanitizePercentageDigits = (raw: string): string => {
  const digitsOnly = raw.replace(/[^0-9.]/g, "");
  const firstDot = digitsOnly.indexOf(".");
  if (firstDot === -1) return digitsOnly;
  return (
    digitsOnly.slice(0, firstDot + 1) +
    digitsOnly.slice(firstDot + 1).replace(/\./g, "").slice(0, 2)
  );
};

// Pads/truncates a fully-typed value to exactly 2 decimal places on blur
// (e.g. "8" -> "8.00", "8.5" -> "8.50") — mirrors getPercentageValue's own
// default toFixedVal of 2, so a saved override always displays with the
// same precision as every live-computed percentage in this table.
const formatToTwoDecimals = (raw: string): string => {
  if (!raw) return raw;
  const num = Number(raw);
  return Number.isNaN(num) ? raw : num.toFixed(2);
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgba(158, 156, 156, 0.1)",
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StrippedTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "rgba(158, 156, 156, 0.1)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "#FAFCFF",
  },
}));

const FilledTableRow = styled(TableRow)(({ theme }) => ({
  borderRadius: "6px",
  background: "#F5F8FF",
}));

const useStyles = makeStyles((theme) => ({
  paper: {
    boxShadow: "none",
  },
}));

interface LeverageTableProps {
  tableHeader: any[];
  tableBody: ObjectType;
  label: string;
  tableClassName?: string;
  // Passed for the "Pricing" adjustments card (every row editable) and the
  // "Leverages" card (only the "Adjusted Max" row editable) — every other
  // LeverageTable consumer (LeveragesAdjustments, ...) is unaffected by
  // these. Edit-mode itself (the pencil/save/cancel icons) is owned by the
  // parent (rendered in the card header, alongside the title — see
  // CustomCard's headingActions) so it can sit on the same line as the card
  // title; this component just renders inputs when told to.
  editable?: boolean;
  overrides?: Record<string, string>;
  // "Adjusted Max" row overrides for the Leverages card, keyed by column
  // header label ("ltvAsIs" / "ltc" / ...) rather than row label — unlike
  // `overrides` above there's only ever one editable row here, so the column
  // is the only thing that needs disambiguating. Same "show even without
  // edit rights, optimistic echo of a just-saved value" purpose as
  // `overrides`.
  leverageOverrides?: Record<string, string>;
  isEditingCard?: boolean;
  draftValues?: Record<string, string>;
  onDraftChange?: (key: string, newValue: string) => void;
}

const LeverageTable: React.FC<LeverageTableProps> = ({
  tableHeader,
  tableBody,
  label,
  tableClassName,
  editable,
  overrides,
  leverageOverrides,
  isEditingCard,
  draftValues,
  onDraftChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const orgFundingType = getCookie("orgFundingType");
  // Same audit-tag convention as every other audited field (GetFieldUI.tsx):
  // a row only shows the history icon once auditedFieldPaths (fetched once
  // per loan load) actually contains its path.
  const { auditedFieldPaths } = useContext(LoansContext) ?? {};

  // Whether an override VALUE should be preferred for display is completely
  // independent of whether THIS viewer is allowed to edit it — a user
  // without update_pricing_adjustment must still see a manually-overridden
  // row's value, not the raw guideline-API one, they just can't change it.
  // Gating display behind `editable` (as canEditRows below does, correctly,
  // for the input/edit-icon UI) was the bug: it silently hid every override
  // from anyone without edit rights, showing the un-overridden API value
  // instead — exactly as if no override had ever been saved.
  const isPricingTable = label === "pricing" && Array.isArray(tableBody) && Array.isArray(tableBody[0]);
  // The Leverages card's tableBody is an array of row objects (each with its
  // own .data array), not an array of tuples like Pricing's — see
  // getLeverageTableBody in src/utils/Common.ts.
  const isLeveragesTable = label === "leverages" && Array.isArray(tableBody) && !Array.isArray(tableBody[0]);
  const canEditRows = editable && (isPricingTable || isLeveragesTable);

  const getResultText = (key: string, parentKey: ObjectType, idx: number) => {
    let className = "";

    if (`${key}`.includes("-") && (!isNaN(+key) || `${key}`.includes("%"))) {
      className = "pass-text";
    } else if (`${key}`.includes("+") && (!isNaN(+key) || `${key}`.includes("%"))) {
      className = "failed-texts";
    }

    const showColouredTexts =
      (parentKey?.data?.length === 3 && parentKey?.data[1] === key) ||
      parentKey?.label === "requested";

    let keyToBeShown = key;
    if (Boolean(keyToBeShown) && /[+-]/.test(keyToBeShown)) {
      if (parentKey?.label?.toLowerCase() === "requested") {
        keyToBeShown = keyToBeShown.replace(/[+-]/g, "");
      }
    }

    let displayValue;
    if (`${keyToBeShown}`.includes("%")) {
      displayValue = keyToBeShown;
    } else if (typeof keyToBeShown === "string" && idx !== 0) {
      displayValue = sanitizeData(t(`${keyToBeShown}`), true, 2);
    } else {
      displayValue = sanitizeData(t(keyToBeShown), true);
    }

    return (
      <span className={showColouredTexts ? className : ""}>
        {displayValue}
      </span>
    );
  };

  const getInterestRateLabel = (data: any, idx: number) => {
    return data === "actualInterestRate" && orgFundingType === "table_funding" && idx === 0
      ? <>Note Rate</>
      : t(data);
  };

  // Renders one cell of the "Leverages" (object-keyed) table body — the
  // "Adjusted Max" row's editable percentage input, or the plain result
  // text (with an audit tag when applicable). Extracted out of
  // renderTableBody (and its onChange/onBlur handlers pulled out into named
  // functions) purely to keep this component's cognitive complexity and
  // closure nesting within Sonar's limits; behavior is unchanged.
  const renderLeverageCell = (key: string, row: ObjectType, data: any, index: number) => {
    // Only the Leverages card's "Adjusted Max" row is ever
    // editable, and only its non-label columns (index 0 is always
    // the row's own display label, e.g. "Adjusted Max").
    const isAdjustedMaxRow = isLeveragesTable && row?.id === "adjustedMax";
    const columnKey = tableHeader?.[index]?.label;
    const isEditableCell =
      canEditRows && isEditingCard && isAdjustedMaxRow && index !== 0;
    const displayValue =
      isAdjustedMaxRow &&
      index !== 0 &&
      leverageOverrides?.[columnKey] !== undefined
        ? leverageOverrides[columnKey]
        : data;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = sanitizePercentageDigits(e.target.value);
      onDraftChange?.(columnKey, digits ? `${digits} %` : "");
    };

    const handleBlur = () => {
      const digits = stripPercentSuffix(draftValues?.[columnKey] ?? "");
      const formatted = formatToTwoDecimals(digits);
      if (formatted && formatted !== digits) {
        onDraftChange?.(columnKey, `${formatted} %`);
      }
    };

    return (
      <StyledTableCell
        style={{
          color: "black",
          borderBottom: "none",
          marginBottom: "8px",
          borderRight: "1px solid #D5DCED",
        }}
        key={`${key}-${index}`}
        className={index === 0 ? row.borderColor : ""}
      >
        {isEditableCell ? (
          <OutlinedInput
            size="small"
            value={stripPercentSuffix(draftValues?.[columnKey] ?? "")}
            onChange={handleChange}
            onBlur={handleBlur}
            endAdornment={<InputAdornment position="end">%</InputAdornment>}
            inputProps={{ inputMode: "decimal" }}
            sx={{
              maxWidth: 100,
              background: "#fff",
              "& .MuiOutlinedInput-input": { textAlign: "right" },
            }}
          />
        ) : isAdjustedMaxRow && index !== 0 ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {getResultText(displayValue, row, index)}
            {auditedFieldPaths?.includes(
              getLeverageAdjustmentAuditFieldPath(columnKey),
            ) ? (
              <AuditTag field={getLeverageAdjustmentAuditFieldPath(columnKey)} />
            ) : null}
          </span>
        ) : (
          getResultText(displayValue, row, index)
        )}
      </StyledTableCell>
    );
  };

  // Renders one cell of the "Pricing" (array-of-tuples) table body.
  // Extracted out of renderTableBody for the same reason as
  // renderLeverageCell above; behavior is unchanged.
  const renderPricingCell = (body: any, data: any, idx: number, index: number) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
      onDraftChange?.(body[0], e.target.value);

    return (
      <StyledTableCell
        style={
          idx === 0 || body.length === 3
            ? {
                color: "black",
                borderBottom: "none",
                marginBottom: "8px",
                padding: isPricingTable
                  ? isEditingCard
                    ? "4px 16px"
                    : "6px 16px"
                  : undefined,
              }
            : {
                color: "black",
                borderBottom: "none",
                marginBottom: "8px",
                display: "flex",
                justifyContent: "end",
                padding: isPricingTable
                  ? isEditingCard
                    ? "4px 16px"
                    : "6px 16px"
                  : undefined,
              }
        }
        key={data}
        sx={{ borderBottom: "none" }}
      >
        <span
          style={
            idx !== 0
              ? {
                  color: "var(--primary-primary-main-700, #40128B)",
                }
              : {}
          }
          className={
            data &&
            idx !== 0 &&
            typeof data === "string" &&
            data.includes("-") &&
            label !== "pricing"
              ? "red-color"
              : ""
          }
        >
          {index === 0 && body[0] === "Final Price Adjustment" ? (
                <b>{idx === 0  ? t("totalPricingAdjustment") : t(data)}</b>
              ) : canEditRows &&
                isEditingCard &&
                idx !== 0 &&
                body[0] !== TOTAL_ROW_LABEL ? (
                <OutlinedInput
                  size="small"
                  value={draftValues?.[body[0]] ?? ""}
                  onChange={handleChange}
                  sx={{
                    maxWidth: 80,
                    background: "#fff",
                    "& .MuiOutlinedInput-input": {
                      padding: "4px 8px",
                    },
                  }}
                />
              ) : isPricingTable && idx !== 0 && body[0] !== TOTAL_ROW_LABEL ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {getInterestRateLabel(
                    overrides?.[body[0]] !== undefined ? overrides[body[0]] : data,
                    idx,
                  )}
                  {auditedFieldPaths?.includes(
                    getPricingAdjustmentAuditFieldPath(body[0]),
                  ) ? (
                    <AuditTag
                      field={getPricingAdjustmentAuditFieldPath(body[0])}
                    />
                  ) : null}
                </span>
              ) : (
                getInterestRateLabel(data, idx)
              )}
        </span>
      </StyledTableCell>
    );
  };

  const renderTableBody = useMemo(() => {
    if (!tableBody) return null;

    if (!Array.isArray(tableBody[0])) {
      return Object.keys(tableBody).map((key) => (
        <StrippedTableRow key={key}>
          {tableBody[key]?.data?.map((data: any, index: number) =>
            renderLeverageCell(key, tableBody[key], data, index),
          )}
        </StrippedTableRow>
      ));
    } else {
      return tableBody.map((body: any, index: number) => (
        <React.Fragment key={body?.[0]}>
          <FilledTableRow
            style={
              isPricingTable
                ? { background: "transparent", borderRadius: 0 }
                : body[0] === "Final Price Adjustment"
                ? { borderRadius: "6px", background: "#E4EAFF" }
                : {}
            }
          >
            {body.map((data: any, idx: number) =>
              renderPricingCell(body, data, idx, index),
            )}
          </FilledTableRow>
        </React.Fragment>
      ));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableBody, t, label, canEditRows, isPricingTable, isLeveragesTable, isEditingCard, draftValues, overrides, leverageOverrides, tableHeader, auditedFieldPaths]);

  return (
    <Paper className={classes.paper}>
      <TableContainer
        sx={{
          padding: "0px 0px",
          borderRadius: "8px",
          fontSize: "1.1rem",
        }}
      >
        <Table className={tableClassName}>
          {tableHeader?.length ? (
            <TableHead
              style={{
                backgroundColor: "#9E9C9C1A",
              }}
            >
              <StrippedTableRow style={{ borderRadius: "8px" }}>
                {tableHeader.map((ele) => (
                  <StyledTableCell
                    className={ele?.headerClassName ?? ""}
                    key={ele?.label}
                  >
                    {t(ele?.label ?? ele)}
                  </StyledTableCell>
                ))}
              </StrippedTableRow>
            </TableHead>
          ) : null}
          <TableBody>{renderTableBody}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default React.memo(LeverageTable);
